/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { SubscriptionProto } from "@noom/noom-contracts/noom_contracts/billing/subscription/v2/subscription";
import type { AddressProto } from "@noom/noom-contracts/noom_contracts/billing/address/address";
import { getActiveUser } from "@noom/hive-auth";
import { fetchBillingUserHomeAddress } from "src/utils/billingData";
import { getActiveProgramSubscription } from "src/utils/api/subscriptions";
import { getProduct } from "src/utils/api/productCatalog";
import { captureException } from "src/utils/error";
import { createAppAsyncThunk } from "../async-thunk";
import getStore from "../store";
import {
  isRequestLoading,
  isRequestSuccess,
  requestState,
  type RequestState,
} from "../requestState";

export interface BillingDataState {
  /**
   * Product Catalog `productName` for {@link activeSubscription}, as resolved
   * from `saleItemId` by billing. `undefined` means not loaded yet; `null`
   * means unavailable (no subscription, or billing could not resolve a name).
   */
  activeSubscriptionProductName?: string | null;
  /** Active program subscription load state from billing APIs. */
  activeSubscription?: RequestState<SubscriptionProto | null>;
  /** Pending end-of-billing-cycle switch target product name load state. */
  pendingSwitchProductName?: RequestState<string | null>;
  /** Billing user's home address load state from billing APIs. */
  homeAddress?: RequestState<AddressProto | null>;
}

const billingDataSlice = createSlice({
  name: "billingData",
  initialState: {} as BillingDataState,
  reducers: {
    setActiveSubscription(
      state,
      action: PayloadAction<RequestState<SubscriptionProto | null>>
    ) {
      state.activeSubscription = action.payload;
    },
    setPendingSwitchProductName(
      state,
      action: PayloadAction<RequestState<string | null>>
    ) {
      state.pendingSwitchProductName = action.payload;
    },
    setActiveSubscriptionProductName(
      state,
      action: PayloadAction<string | null>
    ) {
      state.activeSubscriptionProductName = action.payload;
    },
    setHomeAddress(
      state,
      action: PayloadAction<RequestState<AddressProto | null> | undefined>
    ) {
      if (action.payload === undefined) {
        delete state.homeAddress;
        return;
      }
      state.homeAddress = action.payload;
    },
    resetBillingData: () => ({} as BillingDataState),
  },
});

export const {
  setActiveSubscription,
  setActiveSubscriptionProductName,
  setPendingSwitchProductName,
  setHomeAddress,
  resetBillingData,
} = billingDataSlice.actions;

export default billingDataSlice;

let activeSubscriptionPromise: Promise<void> | null = null;
let pendingSwitchPromise: Promise<void> | null = null;
let homeAddressPromise: Promise<void> | null = null;

/**
 * Incremented on every {@link resetBillingDataCache}. In-flight loaders capture
 * the value at start and skip dispatches after await if it has changed, so
 * stale network results cannot repopulate Redux after a reset.
 */
let billingDataInvalidationGeneration = 0;

function isBillingDataLoadStale(generationAtStart: number): boolean {
  return generationAtStart !== billingDataInvalidationGeneration;
}

/**
 * Clears both Redux billing state and module-level promise caches so the next
 * load call fetches fresh data. Call at flow re-entry points (e.g.
 * onStartCancellationSurvey) to avoid stale data on back-to-back flows.
 *
 * Also bumps an invalidation generation so any async work started before this
 * call will not dispatch when it finishes.
 */
export function resetBillingDataCache() {
  billingDataInvalidationGeneration += 1;
  activeSubscriptionPromise = null;
  pendingSwitchPromise = null;
  homeAddressPromise = null;
  getStore().dispatch(resetBillingData());
}

/** Time given for a purchase to finish writing before the next subscription load. */
export const ACTIVE_SUBSCRIPTION_RETRY_DELAY_MS = 1000;

function waitBeforeSubscriptionRetry() {
  return new Promise((resolve) => {
    setTimeout(resolve, ACTIVE_SUBSCRIPTION_RETRY_DELAY_MS);
  });
}

export const loadActiveSubscription = createAppAsyncThunk<
  { force?: boolean; retries?: number } | void,
  void
>("billingData/loadActiveSubscription", async (options, thunkAPI) => {
  const force = typeof options === "object" && options?.force === true;
  const retries = typeof options === "object" ? options?.retries ?? 0 : 0;
  const { activeSubscription } = thunkAPI.getState().billingData;
  if (
    !force &&
    (isRequestSuccess(activeSubscription) ||
      isRequestLoading(activeSubscription))
  ) {
    return;
  }
  if (!getActiveUser()?.accessCode) {
    return;
  }

  if (!activeSubscriptionPromise) {
    const subscriptionLoadPromise = (async () => {
      const generationAtStart = billingDataInvalidationGeneration;
      try {
        thunkAPI.dispatch(
          setActiveSubscription(
            requestState.loading<SubscriptionProto | null>()
          )
        );

        let subscription:
          | Awaited<ReturnType<typeof getActiveProgramSubscription>>
          | undefined;
        for (let attempt = 0; attempt <= retries; attempt += 1) {
          if (attempt > 0) {
            // eslint-disable-next-line no-await-in-loop
            await waitBeforeSubscriptionRetry();
            if (isBillingDataLoadStale(generationAtStart)) {
              return;
            }
          }
          let failed = false;
          try {
            // eslint-disable-next-line no-await-in-loop
            subscription = await getActiveProgramSubscription();
          } catch (error) {
            if (
              isBillingDataLoadStale(generationAtStart) ||
              attempt === retries
            ) {
              throw error;
            }
            failed = true;
          }
          // No program subscription yet can mean the purchase is still
          // writing. A returned subscription is final.
          if (!failed && (subscription || attempt === retries)) {
            break;
          }
        }
        if (isBillingDataLoadStale(generationAtStart)) {
          return;
        }

        thunkAPI.dispatch(
          setActiveSubscriptionProductName(subscription?.productName ?? null)
        );
        thunkAPI.dispatch(
          setActiveSubscription(
            requestState.success<SubscriptionProto | null>(subscription ?? null)
          )
        );
      } catch (error) {
        captureException(error, "loadActiveSubscription");
        if (isBillingDataLoadStale(generationAtStart)) {
          return;
        }
        thunkAPI.dispatch(
          setActiveSubscription(
            requestState.failure<SubscriptionProto | null>()
          )
        );
      } finally {
        if (activeSubscriptionPromise === subscriptionLoadPromise) {
          activeSubscriptionPromise = null;
        }
      }
    })();
    activeSubscriptionPromise = subscriptionLoadPromise;
  }

  await activeSubscriptionPromise;
});

/**
 * Fetches the active program subscription's pending end-of-billing-cycle switch
 * target product name. Cached: repeated calls share a single in-flight request
 * and skip entirely once Redux is hydrated.
 */
export const loadPendingSwitchData = createAppAsyncThunk<void, void>(
  "billingData/loadPendingSwitchData",
  async (_, thunkAPI) => {
    const { pendingSwitchProductName } = thunkAPI.getState().billingData;
    if (
      isRequestSuccess(pendingSwitchProductName) ||
      isRequestLoading(pendingSwitchProductName)
    ) {
      return;
    }

    if (!pendingSwitchPromise) {
      const switchLoadPromise = (async () => {
        const generationAtStart = billingDataInvalidationGeneration;
        try {
          thunkAPI.dispatch(
            setPendingSwitchProductName(requestState.loading<string | null>())
          );

          await thunkAPI.dispatch(loadActiveSubscription());
          if (isBillingDataLoadStale(generationAtStart)) {
            return;
          }

          const { activeSubscription } = thunkAPI.getState().billingData;

          if (!isRequestSuccess(activeSubscription)) {
            if (isBillingDataLoadStale(generationAtStart)) {
              return;
            }
            thunkAPI.dispatch(
              setPendingSwitchProductName(requestState.failure<string | null>())
            );
            return;
          }

          const lineItems =
            activeSubscription.data?.endOfBillingCycleSwitchDetails?.lineItems;

          if (!lineItems?.length || !lineItems[0].saleItemId) {
            thunkAPI.dispatch(
              setPendingSwitchProductName(
                requestState.success<string | null>(null)
              )
            );
            return;
          }

          const product = await getProduct(lineItems[0].saleItemId);
          if (isBillingDataLoadStale(generationAtStart)) {
            return;
          }

          const productName =
            product?.subscriptionSaleItem?.productName ??
            product?.trialSaleItem?.productName ??
            null;

          thunkAPI.dispatch(
            setPendingSwitchProductName(
              requestState.success<string | null>(productName)
            )
          );
        } catch (error) {
          captureException(error, "loadPendingSwitchData");
          if (isBillingDataLoadStale(generationAtStart)) {
            return;
          }
          thunkAPI.dispatch(
            setPendingSwitchProductName(requestState.failure<string | null>())
          );
        } finally {
          if (pendingSwitchPromise === switchLoadPromise) {
            pendingSwitchPromise = null;
          }
        }
      })();
      pendingSwitchPromise = switchLoadPromise;
    }

    await pendingSwitchPromise;
  }
);

export const loadBillingUserHomeAddress = createAppAsyncThunk<void, void>(
  "billingData/loadHomeAddress",
  async (_, thunkAPI) => {
    const accessCode = getActiveUser()?.accessCode;
    if (!accessCode) {
      return;
    }

    const { homeAddress } = thunkAPI.getState().billingData;
    if (isRequestSuccess(homeAddress) || isRequestLoading(homeAddress)) {
      return;
    }

    if (!homeAddressPromise) {
      const addressLoadPromise = (async () => {
        const generationAtStart = billingDataInvalidationGeneration;
        try {
          thunkAPI.dispatch(
            setHomeAddress(requestState.loading<AddressProto | null>())
          );

          try {
            const resolvedHomeAddress = await fetchBillingUserHomeAddress(
              accessCode
            );
            if (isBillingDataLoadStale(generationAtStart)) {
              return;
            }
            thunkAPI.dispatch(
              setHomeAddress(
                requestState.success<AddressProto | null>(resolvedHomeAddress)
              )
            );
          } catch {
            if (isBillingDataLoadStale(generationAtStart)) {
              return;
            }
            thunkAPI.dispatch(
              setHomeAddress(requestState.failure<AddressProto | null>())
            );
          }
        } finally {
          if (homeAddressPromise === addressLoadPromise) {
            homeAddressPromise = null;
          }
        }
      })();
      homeAddressPromise = addressLoadPromise;
    }

    await homeAddressPromise;
  }
);
